/**
 * AdminShell — sidebar + topbar chrome that wraps every admin view.
 *
 * Design intent: same editorial palette as the public site (bone/sand/ink)
 * but with a distinct sidebar so an admin instantly knows they're in the
 * "back of house." No AI-style icons; Lucide only.
 *
 * The sidebar shows a small badge next to "Verifications" for the pending
 * count — Ops's primary daily workload.
 */

import {
  LayoutDashboard, BadgeCheck, Rocket, Wallet, Users, Home,
  BarChart3, LogOut, Settings, Flag, ShieldAlert, MessageSquareText, Clock, Camera,
} from "lucide-react";

const NAV = [
  { key: "overview",      label: "Overview",       icon: LayoutDashboard, hint: "This week at a glance" },
  { key: "verifications", label: "Verifications",  icon: BadgeCheck,      hint: "Review enrollment proof" },
  { key: "enrollment",    label: "Enrollment changes", icon: ShieldAlert, hint: "Compassionate & academic review" },
  { key: "flagged",       label: "Flagged messages", icon: Flag,          hint: "Review reported wall posts" },
  { key: "campaigns",     label: "Campaigns",      icon: Rocket,          hint: "All active fundraisers" },
  { key: "donations",     label: "Donations & grants", icon: Wallet,      hint: "Gift log + student grants" },
  { key: "updates",       label: "Campaign updates", icon: MessageSquareText, hint: "Moderate & post updates" },
  { key: "extensions",    label: "Extension requests", icon: Clock,       hint: "Approve 30-day extensions" },
  { key: "users",         label: "Users",          icon: Users,           hint: "Students, donors, admins" },
  { key: "curation",      label: "Homepage feed",  icon: Home,            hint: "Choose featured campaigns" },
  { key: "reports",       label: "Reports",        icon: BarChart3,       hint: "Numbers & CSV exports" },
];

export const AdminShell = ({ active, onNavigate, pendingCount, flaggedCount = 0, enrollmentCount = 0, updatesReportedCount = 0, extensionsPendingCount = 0, children }) => (
  <div className="min-h-screen bg-beige text-ink">
    {/* Sidebar */}
    <aside
      data-testid="admin-sidebar"
      className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-ink/10 bg-sand lg:flex"
    >
      <div className="flex items-center gap-3 px-6 pt-6 pb-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-sand">
          <span className="font-serif text-lg leading-none">M</span>
        </div>
        <div>
          <p className="font-serif text-lg leading-none text-ink">My Rich Aunt</p>
          <p className="mt-0.5 text-[10px] uppercase tracking-[0.22em] text-ink/60">Admin</p>
        </div>
      </div>

      <p className="mt-4 px-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/50">Workspace</p>
      <nav className="mt-2 flex-1 space-y-1 px-3">
        {NAV.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.key;
          const badgeCount =
            item.key === "verifications" ? pendingCount :
            item.key === "flagged"       ? flaggedCount :
            item.key === "enrollment"    ? enrollmentCount :
            item.key === "updates"       ? updatesReportedCount :
            item.key === "extensions"    ? extensionsPendingCount : 0;
          const showBadge = badgeCount > 0;
          return (
            <button
              key={item.key}
              type="button"
              data-testid={`admin-nav-${item.key}`}
              onClick={() => onNavigate(item.key)}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${
                isActive
                  ? "bg-ink text-sand"
                  : "text-ink hover:bg-ink/5"
              }`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-sand" : "text-ink/70"}`} strokeWidth={1.75} />
              <div className="flex-1">
                <span className="block text-sm font-medium leading-tight">{item.label}</span>
                <span className={`mt-0.5 block text-[11px] leading-tight ${isActive ? "text-sand/70" : "text-ink/50"}`}>
                  {item.hint}
                </span>
              </div>
              {showBadge && (
                <span
                  data-testid="admin-nav-badge"
                  className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
                    isActive ? "bg-sand text-ink" : "bg-tangerine text-sand"
                  }`}
                >
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="border-t border-ink/10 px-3 py-4">
        <a
          href="/admin/launch"
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink hover:bg-ink/5"
          data-testid="admin-nav-launch"
        >
          <Rocket className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
          Launch day checklist
        </a>
        <a
          href="/admin/founding"
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink hover:bg-ink/5"
          data-testid="admin-nav-founding"
        >
          <Users className="h-4 w-4 text-ink/70" strokeWidth={1.75} />
          Founding donors ledger
        </a>
        <a
          href="/admin/team"
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink hover:bg-ink/5"
          data-testid="admin-nav-team"
        >
          <Camera className="h-4 w-4 text-ink/70" strokeWidth={1.75} />
          Team page (photos &amp; bios)
        </a>
        <button
          type="button"
          onClick={() => onNavigate("settings")}
          className={`mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink hover:bg-ink/5 ${active === "settings" ? "bg-ink/5 font-medium" : ""}`}
          data-testid="admin-nav-settings"
        >
          <Settings className="h-4 w-4 text-ink/70" strokeWidth={1.75} />
          Settings · Platform policy
        </button>
        <button
          type="button"
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink hover:bg-ink/5"
          data-testid="admin-nav-signout"
        >
          <LogOut className="h-4 w-4 text-ink/70" strokeWidth={1.75} />
          Sign out
        </button>
      </div>
    </aside>

    {/* Topbar (mobile shows brand + view name; desktop shows breadcrumb) */}
    <header className="sticky top-0 z-30 border-b border-ink/10 bg-beige/85 backdrop-blur lg:pl-72">
      <div className="flex items-center justify-between gap-4 px-6 py-4 lg:px-10">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/50">Admin</p>
          <p className="font-serif text-xl leading-tight text-ink">
            {NAV.find((n) => n.key === active)?.label || "Overview"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <p className="text-sm font-medium text-ink">Terri James</p>
            <p className="text-[11px] text-ink/60">Founder · Admin</p>
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-tangerine/20 font-serif text-sm text-tangerine ring-1 ring-tangerine/40">
            TJ
          </div>
        </div>
      </div>
    </header>

    {/* Content */}
    <main className="lg:pl-72">
      <div className="mx-auto max-w-6xl px-6 py-10 lg:px-10 lg:py-14">
        {children}
      </div>
    </main>
  </div>
);
